import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { Box, Button, Modal, Typography } from "@mui/material";
import { error } from "../../theme/themePrimitives";
import { setAxiosUserEmail } from "../../services/api/axiosInstance";

const SYSTEM_SIGNOUT_MESSAGE =
  "Signed out: session expired or active elsewhere. Log in to continue.";

const GlobalErrorHandler: React.FC = () => {
  const [openSystemSignOut, setOpenSystemSignOut] = useState(false);
  const [systemSignOutMessage, setSystemSignOutMessage] = useState(
    SYSTEM_SIGNOUT_MESSAGE,
  );

  const navigate = useNavigate();
  const { dispatch } = useAuth();
  const { showToast } = useToast();

  useEffect(() => {
    const handleClearUser = () => {
      localStorage.removeItem("userName");
      localStorage.removeItem("rememberMe");
      sessionStorage.removeItem("selectedPortal");
      sessionStorage.removeItem("selectedPortalName");
      sessionStorage.removeItem("selectedOwnersFilter");
      localStorage.removeItem("selectedUsers");
      localStorage.removeItem("selectedUsersIds");
      localStorage.removeItem("manualSelectedUsers");
      localStorage.removeItem("manualSelectedUsersIds");
      localStorage.removeItem("filteredSelectedUsers");
      localStorage.removeItem("filteredSelectedUsersIds");
      localStorage.removeItem("filteredUserSelections");
      localStorage.removeItem("selectedUsersSource");
      localStorage.removeItem("fileHubSelectedFiles");
      localStorage.removeItem("fileHubAppliedFiltersList");

      setAxiosUserEmail(null);
      dispatch({ type: "CLEAR_USER" });
    };

    const handleShowToast = (event: CustomEvent) => {
      const { message, type, duration } = event.detail;
      showToast(message, type, duration);
    };

    const handleRedirectToLogin = () => {
      if (window.location.pathname !== "/login") {
        navigate("/login", { replace: true });
      }
    };

    const handleSystemSignOut = (event: CustomEvent) => {
      setSystemSignOutMessage(event.detail?.message ?? SYSTEM_SIGNOUT_MESSAGE);
      setOpenSystemSignOut(true);
    };

    window.addEventListener("clearUser", handleClearUser as EventListener);
    window.addEventListener("showToast", handleShowToast as EventListener);
    window.addEventListener(
      "redirectToLogin",
      handleRedirectToLogin as EventListener,
    );
    window.addEventListener(
      "systemSignOut",
      handleSystemSignOut as EventListener,
    );

    return () => {
      window.removeEventListener("clearUser", handleClearUser as EventListener);
      window.removeEventListener("showToast", handleShowToast as EventListener);
      window.removeEventListener(
        "redirectToLogin",
        handleRedirectToLogin as EventListener,
      );
      window.removeEventListener(
        "systemSignOut",
        handleSystemSignOut as EventListener,
      );
    };
  }, [dispatch, showToast, navigate]);

  const handleLoginClick = () => {
    setOpenSystemSignOut(false);

    window.dispatchEvent(new CustomEvent("clearUser"));
    window.dispatchEvent(new CustomEvent("authErrorHandled"));
    window.dispatchEvent(new CustomEvent("redirectToLogin"));
  };

  return (
    <>
      <Modal
        open={openSystemSignOut}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          return;
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 544,
            maxWidth: "calc(100vw - 32px)",
            bgcolor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
            outline: "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "24px 24px 4px 24px",
            }}
          >
            <Typography
              sx={{ fontSize: 18, fontWeight: 600, color: "#101828" }}
            >
              You have been signed out
            </Typography>
          </Box>

          {/* Body text */}
          <Box sx={{ p: "0 24px 10px 24px" }}>
            <Typography
              sx={{
                fontSize: 15,
                color: "#4B5563",
              }}
            >
              {systemSignOutMessage}
            </Typography>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: "16px 24px",
            }}
          >
            <Button
              onClick={handleLoginClick}
              sx={{
                backgroundColor: error[600],
                color: "#ffffff",
                fontWeight: 600,
                p: "10px 16px",
                borderRadius: "8px",
                "&:hover": {
                  bgcolor: `${error[700]} !important`,
                },
              }}
            >
              Sign in again
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default GlobalErrorHandler;
